import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown, faBars, faXmark, faCheck, faBolt, faClock, faStar,
  faBox, faLocationDot, faVideo, faUserPlus, faWandMagicSparkles,
  faPlay, faArrowRight, faStarOfLife, faArrowTrendUp, faHeart, faSackDollar,
  faEuroSign, faEye, faUsers, faChevronRight
} from '@fortawesome/free-solid-svg-icons'
import { faTiktok, faInstagram, faFacebookF } from '@fortawesome/free-brands-svg-icons'
import TikTokEmbed from '../components/TikTokEmbed'
import './NewHomePage.css'

// Fallback creators for the showcase carousel — only used if API fetch fails.
const fallbackCreators = [
  { name: 'Luca', rank: 'amb', photo: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&q=80', live: true, crown: true },
  { name: 'Tibor', rank: 'amb', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&q=80', live: true, crown: true },
  { name: 'Jaimie', rank: 'rising', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&q=80', live: false, crown: false },
  { name: 'Timo', rank: 'rising', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80', live: false, crown: false },
  { name: 'Kaya', rank: 'trend', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&q=80', live: false, crown: false },
  { name: 'Wesley', rank: 'rising', photo: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=600&q=80', live: false, crown: false },
  { name: 'Rebecca', rank: 'trend', photo: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=600&q=80', live: true, crown: false },
  { name: 'Tim', rank: 'rising', photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&q=80', live: false, crown: false },
  { name: 'Sara', rank: 'rookie', photo: 'https://images.unsplash.com/photo-1554151228-14d9def656e4?w=600&q=80', live: false, crown: false },
  { name: 'Eva', rank: 'trend', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&q=80', live: true, crown: false },
]

// Convert API creator → carousel card shape.
const toShowcaseCard = (c) => {
  const completed = c?.stats?.completedProjects || 0
  let rank = 'rookie'
  if (c.isAmbassador) rank = 'amb'
  else if (completed > 10) rank = 'trend'
  else if (completed > 0) rank = 'rising'
  // First name only for privacy, capitalized
  const raw = (c.name || '').trim().split(' ')[0] || 'Creator'
  const firstName = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
  return {
    name: firstName,
    rank,
    photo: c.profilePictureUrl,
    live: c.availability === 'within-day' || c.availability === 'now',
    crown: c.isAmbassador,
  }
}

const knowledgeLinks = [
  { href: '/wat-is-ugc',                 t: 'Wat is UGC?',                d: 'Complete gids over User Generated Content' },
  { href: '/ugc-marketing-strategie',    t: 'UGC Marketing Strategie',    d: '7-stappen plan voor succesvolle campagnes' },
  { href: '/ugc-voorbeelden',            t: 'UGC Voorbeelden',            d: '50+ inspirerende voorbeelden per industrie' },
  { href: '/ugc-vs-influencer-marketing',t: 'UGC vs Influencer Marketing',d: 'Complete vergelijking en keuzewijzer' },
  { href: '/ugc-campagne-opzetten',      t: 'UGC Campagne Opzetten',      d: 'Stap-voor-stap gids + templates' },
  { href: '/ugc-tips-creators',          t: 'UGC Tips voor Creators',     d: '25+ praktische tips om te excelleren' },
  { href: '/ugc-trends-2026',            t: 'UGC Trends 2026',            d: 'Laatste trends en ontwikkelingen' },
]
const knowledgeServices = [
  { href: '/content-creator-inhuren',    t: 'Content Creator Inhuren',  d: 'Vind de juiste creator voor je merk' },
  { href: '/social-media-uitbesteden',   t: 'Social Media Uitbesteden', d: 'Content voor Instagram, TikTok & meer' },
  { href: '/voor-bedrijven',            t: 'UGC Laten Maken',          d: 'Authentieke video & foto vanaf €25' },
  { href: '/ugc-bureau',                 t: 'UGC Bureau',               d: 'Professionele UGC diensten' },
  { href: '/ugc-prijzen',                t: 'Prijzen & Pakketten',      d: 'Flexibel budget, jij bepaalt' },
  { href: '/voor-bedrijven',             t: 'Voor Bedrijven',           d: 'Zo werkt UGC4you voor jouw merk' },
  { href: '/ugc-content-creators',       t: 'Word UGC Creator',         d: 'Verdien geld met content maken' },
]

const Logo = () => (
  <span className="logo">UGC<b>4you</b></span>
)

const NewHomePage = () => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  // Quick-brief widget state — laagdrempelig opdracht plaatsen vanaf homepage
  const [briefDescription, setBriefDescription] = useState('')
  const [briefChannels, setBriefChannels] = useState([])
  const [briefBudget, setBriefBudget] = useState('')
  const [briefDeadline, setBriefDeadline] = useState('')
  const [showBriefPreview, setShowBriefPreview] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isKnowledgeOpen, setIsKnowledgeOpen] = useState(false)
  const [isMobileKnowledgeOpen, setIsMobileKnowledgeOpen] = useState(false)
  const [creators, setCreators] = useState(fallbackCreators)
  const [creatorCount, setCreatorCount] = useState(null)
  const [portfolioCreators, setPortfolioCreators] = useState([])
  const [cfCenter, setCfCenter] = useState(0)
  const [cfMuted, setCfMuted] = useState(true)
  const cfTimerRef = useRef(null)
  // Showcase-rail: op mobiel (≤768px) native horizontale scroller i.p.v. CSS-marquee
  const [railTouch, setRailTouch] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  const railMaskRef = useRef(null)
  const railPauseUntil = useRef(0)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = (e) => setRailTouch(e.matches)
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange) }
  }, [])
  // Mobiel: rustig automatisch doorschuiven, pauzeert zodra de gebruiker zelf veegt
  useEffect(() => {
    if (!railTouch) return
    const el = railMaskRef.current
    if (!el) return
    const onTouch = () => { railPauseUntil.current = Date.now() + 8000 }
    el.addEventListener('touchstart', onTouch, { passive: true })
    el.addEventListener('touchend', onTouch, { passive: true })
    const id = setInterval(() => {
      if (Date.now() < railPauseUntil.current) return
      const card = el.querySelector('.creator-card')
      if (!card) return
      const step = card.getBoundingClientRect().width + 14
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + step, behavior: 'smooth' })
    }, 3500)
    return () => {
      clearInterval(id)
      el.removeEventListener('touchstart', onTouch)
      el.removeEventListener('touchend', onTouch)
    }
  }, [railTouch, creators.length])
  const cfStageRef = useRef(null)
  const cfVideoRefs = useRef([])
  const knowledgeRef = useRef(null)

  // Fetch real creators with photos for the showcase carousel + coverflow
  useEffect(() => {
    let cancelled = false
    fetch('/api/users/creators?limit=50&sortBy=newest')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.creators?.length) return
        const withPhoto = data.creators.filter(c => c.profilePictureUrl)
        if (withPhoto.length >= 6) {
          setCreators(withPhoto.map(toShowcaseCard))
        }
        // Coverflow: creators with at least one portfolio video
        const withVideo = data.creators.filter(c =>
          c.profilePictureUrl &&
          Array.isArray(c.portfolio) &&
          c.portfolio.some(p => p?.videoUrl)
        )
        if (withVideo.length >= 3) {
          setPortfolioCreators(withVideo.slice(0, 7))
        }
        if (data.pagination?.totalCreators) setCreatorCount(data.pagination.totalCreators)
      })
      .catch(() => { /* keep fallback */ })
    return () => { cancelled = true }
  }, [])

  // Coverflow auto-rotate + video play/pause control
  useEffect(() => {
    if (portfolioCreators.length === 0) return
    const N = portfolioCreators.length
    cfVideoRefs.current.forEach((v, i) => {
      if (!v) return
      if (i === cfCenter) {
        v.muted = cfMuted
        v.play().catch(() => {
          // If autoplay with sound is blocked, fall back to muted playback
          if (!cfMuted) {
            v.muted = true
            v.play().catch(() => {})
          }
        })
      } else {
        v.muted = true
        v.pause()
      }
    })
    cfTimerRef.current = setInterval(() => {
      setCfCenter(c => (c + 1) % N)
    }, 4500)
    return () => clearInterval(cfTimerRef.current)
  }, [cfCenter, cfMuted, portfolioCreators.length])

  // Pause when tab hidden
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        clearInterval(cfTimerRef.current)
        cfVideoRefs.current.forEach(v => v && v.pause())
      } else {
        const v = cfVideoRefs.current[cfCenter]
        v && v.play().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [cfCenter])

  const cfGoTo = (i) => {
    const N = portfolioCreators.length
    if (N === 0) return
    setCfCenter(((i % N) + N) % N)
  }
  const cfPrev = () => cfGoTo(cfCenter - 1)
  const cfNext = () => cfGoTo(cfCenter + 1)

  // Touch swipe (mobiel): links/rechts vegen = vorige/volgende kaart
  const cfTouchRef = useRef(null)
  const cfRestartTimer = () => {
    clearInterval(cfTimerRef.current)
    cfTimerRef.current = setInterval(() => {
      setCfCenter(c => (c + 1) % portfolioCreators.length)
    }, 4500)
  }
  const onCfTouchStart = (e) => {
    const t = e.touches[0]
    cfTouchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() }
    clearInterval(cfTimerRef.current)
  }
  const onCfTouchEnd = (e) => {
    const start = cfTouchRef.current
    cfTouchRef.current = null
    if (!start) { cfRestartTimer(); return }
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    const dt = Date.now() - start.t
    // Horizontale veeg: minimaal 40px (of snelle flick) en duidelijk horizontaler dan verticaal
    if (Math.abs(dx) > Math.abs(dy) * 1.2 && (Math.abs(dx) > 40 || (Math.abs(dx) > 20 && dt < 250))) {
      if (dx < 0) cfNext(); else cfPrev()
    }
    cfRestartTimer()
  }

  // First-name only, capitalized
  const firstName = (full) => {
    const raw = (full || '').trim().split(' ')[0] || 'Creator'
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
  }
  const nicheLabel = (c) => {
    const n = (c.niches || []).slice(0, 2)
    if (n.length === 0) return 'UGC Creator'
    return n.map(x => x.charAt(0).toUpperCase() + x.slice(1)).join(' · ')
  }
  const cfRank = (c) => {
    const completed = c?.stats?.completedProjects || 0
    if (c.isAmbassador) return 'amb'
    if (completed > 10) return 'trend'
    if (completed > 0) return 'rising'
    return 'rookie'
  }
  const isLive = (c) => c?.availability === 'within-day' || c?.availability === 'now'
  const cfPos = (i) => {
    const N = portfolioCreators.length
    let diff = i - cfCenter
    if (diff > N / 2) diff -= N
    if (diff < -N / 2) diff += N
    return diff
  }

  // Close knowledge dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (knowledgeRef.current && !knowledgeRef.current.contains(e.target)) {
        setIsKnowledgeOpen(false)
      }
    }
    if (isKnowledgeOpen) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [isKnowledgeOpen])

  // Close mobile menu on route hash change
  const closeMobile = () => { setIsMobileMenuOpen(false); setIsMobileKnowledgeOpen(false) }
  const navRef = useRef(null)

  // Mobiel menu: body-scroll-lock, sluiten op Escape, en menu-top gelijk aan de echte nav-hoogte
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined
    if (navRef.current) {
      document.documentElement.style.setProperty('--ugc-nav-h', `${navRef.current.offsetHeight}px`)
    }
    const onKey = (e) => { if (e.key === 'Escape') closeMobile() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [isMobileMenuOpen])

  const rankClass = (r) => ({ amb: 'rank-amb', trend: 'rank-trend', rising: 'rank-rising', rookie: 'rank-rookie' }[r] || 'rank-rookie')

  return (
    <div className="ugc-home">
      {/* ==================== NAV ==================== */}
      <nav className="nav" ref={navRef}>
        <div className="nav-inner">
          <Link to="/" onClick={closeMobile}><Logo /></Link>

          <div className="nav-links">
            <a href="#hoe-werkt-het">Hoe werkt het</a>
            <a href="#eerlijk-model">Ons model</a>
            <a href="#voor-wie">Voor wie</a>
            <div className="knowledge-dropdown" ref={knowledgeRef}>
              <button onClick={() => setIsKnowledgeOpen(v => !v)} aria-expanded={isKnowledgeOpen}>
                Kennisbank
                <FontAwesomeIcon icon={faChevronDown} style={{ width: 12, height: 12, transform: isKnowledgeOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {isKnowledgeOpen && (
                <div className="knowledge-menu" role="menu">
                  <div className="sec-label">Leer over UGC</div>
                  {knowledgeLinks.map(l => (
                    <a key={l.href} href={l.href} role="menuitem">
                      <div className="t">{l.t}</div>
                      <div className="d">{l.d}</div>
                    </a>
                  ))}
                  <div className="sec-label" style={{ marginTop: 6 }}>Services</div>
                  {knowledgeServices.map(l => (
                    <a key={l.href} href={l.href} role="menuitem">
                      <div className="t">{l.t}</div>
                      <div className="d">{l.d}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="nav-right">
            {!isAuthenticated ? (
              <>
                <Link to="/login" className="btn btn-ghost desktop-only">Inloggen</Link>
                <Link to="/register" className="btn btn-green">Gratis aanmelden</Link>
              </>
            ) : (
              <Link to="/dashboard" className="btn btn-green">Dashboard</Link>
            )}
            <button
              className="nav-mobile-btn"
              onClick={() => setIsMobileMenuOpen(v => !v)}
              aria-label={isMobileMenuOpen ? 'Menu sluiten' : 'Menu openen'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="ugc-mobile-menu"
            >
              <FontAwesomeIcon icon={isMobileMenuOpen ? faXmark : faBars} style={{ width: 22, height: 22 }} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu — outside <nav> because .nav has backdrop-filter,
          which makes it a containing block for fixed descendants and would
          clip the menu to the nav's own 64px height. */}
      <div id="ugc-mobile-menu" className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <a href="#hoe-werkt-het" onClick={closeMobile}>Hoe werkt het</a>
        <a href="#eerlijk-model" onClick={closeMobile}>Ons model</a>
        <a href="#voor-wie" onClick={closeMobile}>Voor wie</a>
        <button onClick={() => setIsMobileKnowledgeOpen(v => !v)} aria-expanded={isMobileKnowledgeOpen}>
          Kennisbank <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 6, width: 12, height: 12, transform: isMobileKnowledgeOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        </button>
        {isMobileKnowledgeOpen && (
          <div className="submenu">
            {[...knowledgeLinks, ...knowledgeServices].map(l => (
              <a key={l.href} href={l.href} onClick={closeMobile}>{l.t}</a>
            ))}
          </div>
        )}
        <div className="cta-stack">
          {!isAuthenticated ? (
            <>
              <Link to="/login" className="btn btn-outline btn-lg" onClick={closeMobile}>Inloggen</Link>
              <Link to="/register" className="btn btn-green btn-lg" onClick={closeMobile}>Gratis aanmelden</Link>
            </>
          ) : (
            <Link to="/dashboard" className="btn btn-green btn-lg" onClick={closeMobile}>Dashboard</Link>
          )}
        </div>
      </div>

      {/* ==================== HERO ==================== */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="hero-grid">
          <div>
            <div className="pill">
              <span className="pill-dot"></span>
              50+ creators klaar om jouw content te maken
            </div>
            <h1 className="hero-title">
              Social media content die <span className="grad">werkt.</span>
              <span className="mobile-only"> voor jouw bedrijf</span>
            </h1>
            <p className="hero-sub">
              Ontvang <b>binnen 48 uur</b> video's die je direct kunt inzetten op TikTok, Instagram en ads. <b>Jij bepaalt het budget.</b>
            </p>

            <div className="platforms">
              <div className="chip"><span className="chip-ico ico-tt"><FontAwesomeIcon icon={faTiktok} style={{ width: 11, height: 11 }} /></span>TikTok</div>
              <div className="chip"><span className="chip-ico ico-meta"><FontAwesomeIcon icon={faFacebookF} style={{ width: 11, height: 11 }} /></span>Meta</div>
              <div className="chip"><span className="chip-ico ico-ig"><FontAwesomeIcon icon={faInstagram} style={{ width: 12, height: 12 }} /></span>Instagram</div>
            </div>

            <div className="hero-cta">
              <Link to="/register?role=business" className="btn btn-green btn-lg">
                Plaats je eerste opdracht <FontAwesomeIcon icon={faArrowRight} style={{ width: 13, height: 13 }} />
              </Link>
              <Link to="/register?role=creator" className="btn btn-outline btn-lg">Meld je aan als creator</Link>
            </div>

            <div className="micro">
              <span><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Betaal na goedkeuring</span>
              <span><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Gratis revisie</span>
              <span><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Geen verplichtingen</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-photo">
              <img src="/ugc-hero-image.webp" alt="UGC Creator aan het werk" />
              <div className="photo-overlay"></div>
              <div className="badge-tl"><span className="live"></span> Live shoot · vandaag</div>
              <div className="badge-price">Vanaf <b>€75</b></div>
            </div>
            <div className="float-card float-stat">
              <div className="lbl">Laatste resultaat</div>
              <div className="val">3 video's</div>
              <div className="sub">geleverd in 2 dagen</div>
            </div>
            <div className="float-card float-rev">
              <div className="head">
                <div className="stars">★★★★★</div>
                <small>5.0</small>
              </div>
              <h4>Precies wat we zochten</h4>
              <p>"Onze ads-conversie ging met 3× omhoog."</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TRUST BAR ==================== */}
      <div className="trustbar">
        <div className="trustbar-inner">
          <div className="trust-lbl">Vertrouwd door bedrijven in heel Nederland</div>
          <div className="trust-stats">
            <div className="ts"><div className="ts-ic"><FontAwesomeIcon icon={faBolt} style={{ width: 13, height: 13 }} /></div><div><b>48u</b> levertijd</div></div>
            <div className="ts"><div className="ts-ic"><FontAwesomeIcon icon={faStar} style={{ width: 13, height: 13 }} /></div><div><b>4.8/5</b> score</div></div>
            <div className="ts"><div className="ts-ic">€</div><div>Vanaf <b>€75</b></div></div>
            <div className="ts"><div className="ts-ic"><FontAwesomeIcon icon={faStarOfLife} style={{ width: 12, height: 12 }} /></div><div><b>1000+</b> creators</div></div>
          </div>
        </div>
      </div>

      {/* ==================== MARQUEE ==================== */}
      <div className="marquee">
        <div className="marquee-track">
          <span>
            Jij bepaalt het budget <span className="dot">·</span>
            Levering binnen 48u <span className="dot">·</span>
            Nederlands platform <span className="dot">·</span>
            Betaal na goedkeuring <span className="dot">·</span>
            1000+ creators <span className="dot">·</span>
            Gratis revisie <span className="dot">·</span>
            Authentieke content <span className="dot">·</span>
          </span>
          <span>
            Jij bepaalt het budget <span className="dot">·</span>
            Levering binnen 48u <span className="dot">·</span>
            Nederlands platform <span className="dot">·</span>
            Betaal na goedkeuring <span className="dot">·</span>
            1000+ creators <span className="dot">·</span>
            Gratis revisie <span className="dot">·</span>
            Authentieke content <span className="dot">·</span>
          </span>
        </div>
      </div>

      {/* ==================== CREATOR SHOWCASE RAIL ==================== */}
      <section className="showcase">
        <div className="showcase-head">
          <div>
            <div className="sec-eyebrow" style={{ marginBottom: 6 }}><span className="e-dot"></span> Maak kennis met onze creators</div>
            <h2>1000+ Nederlandse <span className="grn">creators</span> beschikbaar</h2>
            <p>Van rising stars tot ambassadors — ontdek de creators die jouw merk tot leven brengen.</p>
          </div>
        </div>

        <div className={`rail-mask ${railTouch ? 'rail-touch' : ''}`} ref={railMaskRef}>
          <div className="rail">
            {(railTouch ? creators.slice(0, 16) : [...creators.slice(0, 16), ...creators.slice(0, 16)]).map((c, i) => {
              return (
              <div style={{ cursor: 'default' }} aria-disabled="true" key={i} className="creator-card">
                <img className="thumb" src={c.photo} alt={c.name} loading="lazy" decoding="async" />
                <div className="grad-top"></div>
                <div className="grad-bot"></div>
                <div className="creator-info">
                  <div className="creator-av" style={{ backgroundImage: `url('${c.photo.replace('w=600', 'w=120')}')` }}>
                    <div className={`creator-rank ${rankClass(c.rank)}`}>★</div>
                  </div>
                  <div className="creator-meta">
                    <span className="creator-name">{c.name}{c.crown && <span className="crown">👑</span>}</span>
                    <div className="creator-stars">★★★★★</div>
                  </div>
                </div>
                <div className="creator-bottom">
                  {c.live ? <span className="creator-tag">Live</span> : <span />}
                  <div className="creator-play"><FontAwesomeIcon icon={faPlay} style={{ width: 12, height: 12 }} /></div>
                </div>
              </div>
              )
            })}
          </div>
        </div>

        <div className="showcase-foot">
          <span><span className="live-dot"></span> <b>{creators.filter(c => c.live).length || creators.length}</b> creators recent actief</span>
          <span>•</span>
          <span>Meer dan <b>150+</b> creators beschikbaar</span>
        </div>
      </section>

      {/* ==================== QUICK BRIEF WIDGET ==================== */}
      <section className="quick-brief-section">
        <div className="qb-container">
          <div className="qb-header">
            <div className="sec-eyebrow" style={{ marginBottom: 6 }}>
              <span className="e-dot"></span> Plaats je eerste brief in 2 minuten
            </div>
            <h2>Probeer het direct — <span className="grn">geen account nodig</span></h2>
            <p>
              Beschrijf wat je zoekt. Wij laten zien hoe je brief eruit komt te zien op de marketplace.
              Daarna kun je hem live zetten met één klik.
            </p>
          </div>

          <div className="qb-card">
            <div className="qb-form">
              <div className="qb-field">
                <label>Wat voor content zoek je?</label>
                <textarea
                  value={briefDescription}
                  onChange={(e) => setBriefDescription(e.target.value)}
                  placeholder="Bv: Korte TikTok video over onze nieuwe gezichtscrème. Vrolijke vibe, gebruik in alledaagse setting. Doel: conversie naar webshop."
                  rows={4}
                  maxLength={500}
                />
                <div className="qb-counter">{briefDescription.length}/500</div>
              </div>

              <div className="qb-row">
                <div className="qb-field">
                  <label>Kanalen</label>
                  <div className="qb-chips">
                    {['TikTok', 'Instagram', 'YouTube Shorts', 'Meta Ads'].map(ch => (
                      <button
                        key={ch}
                        type="button"
                        className={`qb-chip ${briefChannels.includes(ch) ? 'active' : ''}`}
                        onClick={() => setBriefChannels(prev =>
                          prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
                        )}
                      >
                        {ch}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="qb-row">
                <div className="qb-field qb-half">
                  <label>Budget (€, excl. BTW)</label>
                  <input
                    type="number"
                    min={25}
                    value={briefBudget}
                    onChange={(e) => setBriefBudget(e.target.value)}
                    placeholder="bv. 250"
                  />
                  <div className="qb-hint">Minimaal €25 · gemiddeld €150-€500</div>
                </div>
                <div className="qb-field qb-half">
                  <label>Gewenste levering</label>
                  <input
                    type="date"
                    value={briefDeadline}
                    onChange={(e) => setBriefDeadline(e.target.value)}
                    min={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  />
                  <div className="qb-hint">Minimaal 7 dagen vanaf nu</div>
                </div>
              </div>

              <button
                type="button"
                className="qb-preview-btn"
                disabled={briefChannels.length === 0 || !briefBudget || !briefDeadline}
                onClick={() => setShowBriefPreview(true)}
              >
                Bekijk hoe je brief eruit komt te zien
                <FontAwesomeIcon icon={faArrowRight} style={{ width: 14, height: 14, marginLeft: 8 }} />
              </button>
              <div className="qb-note">
                Geen verplichtingen — gratis plaatsen, pas 10% aanbetaling als je een creator kiest.
              </div>
            </div>

            {/* Preview pane — verschijnt na klik op bekijken */}
            {showBriefPreview && (
              <div className="qb-preview">
                <div className="qb-preview-label">📋 Zo ziet je dashboard eruit na het plaatsen:</div>
                {/* Mini-mockup van het echte dashboard met sidebar */}
                <div className="qb-mockup">
                  {/* Browser-style chrome */}
                  <div className="qb-mockup-chrome">
                    <span className="qb-mockup-dot red"></span>
                    <span className="qb-mockup-dot yellow"></span>
                    <span className="qb-mockup-dot green"></span>
                    <div className="qb-mockup-url">ugc4you.nl/dashboard</div>
                  </div>
                  <div className="qb-mockup-body">
                    {/* Sidebar */}
                    <aside className="qb-mockup-sidebar">
                      <div className="qb-mockup-logo">UGC<span>4you</span></div>
                      <nav className="qb-mockup-nav">
                        <div className="qb-mockup-navitem"><span className="qb-mi-ic">🏠</span> Home</div>
                        <div className="qb-mockup-navitem active"><span className="qb-mi-ic">📋</span> Mijn Opdrachten</div>
                        <div className="qb-mockup-navitem"><span className="qb-mi-ic">👥</span> Creators</div>
                        <div className="qb-mockup-navitem"><span className="qb-mi-ic">💬</span> Berichten</div>
                        <div className="qb-mockup-navitem"><span className="qb-mi-ic">📊</span> Analytics</div>
                        <div className="qb-mockup-navitem"><span className="qb-mi-ic">⚙️</span> Instellingen</div>
                      </nav>
                    </aside>
                    {/* Main content area */}
                    <main className="qb-mockup-main">
                      <div className="qb-mockup-topbar">
                        <span className="qb-mockup-crumb">Mijn Opdrachten</span>
                        <div className="qb-mockup-avatar">JB</div>
                      </div>
                      <div className="qb-mockup-content">
                        <h2 className="qb-mockup-h2">Mijn Opdrachten <span className="qb-mockup-count">1</span></h2>
                        <p className="qb-mockup-sub">Beheer al je geplaatste opdrachten</p>

                        {/* De échte opdracht-card (zoals in MyProjectsPage) */}
                        <div className="qb-mockup-card-grid">
                          <div className="qb-mockup-card">
                            <div className="qb-mockup-card-top">
                              <span className="qb-mockup-status"><span className="qb-mockup-status-dot"></span> Live op marktplaats</span>
                              <span className="qb-mockup-prop">👥 0</span>
                            </div>
                            <div className="qb-mockup-card-title">
                              {briefDescription.trim()
                                ? (briefDescription.split(/[.!?\n]/)[0] || 'Jouw UGC opdracht').slice(0, 60) + (briefDescription.length > 60 ? '…' : '')
                                : 'Jouw UGC opdracht'}
                            </div>
                            <div className="qb-mockup-card-desc">
                              {briefDescription.trim()
                                ? briefDescription.slice(0, 120) + (briefDescription.length > 120 ? '…' : '')
                                : <em style={{ color: '#94A3B8' }}>Op de volgende pagina vul je de uitgebreide omschrijving in.</em>}
                            </div>
                            <div className="qb-mockup-card-meta">
                              <span>💰 €{briefBudget ? (Number(briefBudget) * 1.21).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'} <em>incl. BTW</em></span>
                              <span>📅 {briefDeadline ? Math.max(0, Math.ceil((new Date(briefDeadline) - new Date()) / (1000*60*60*24))) + 'd' : '–'}</span>
                              <span>🕒 Zojuist</span>
                            </div>
                            <div className="qb-mockup-card-btn">👁 Bekijken</div>
                          </div>

                          {/* Een grayed-out placeholder card om te suggereren dat er meer komen */}
                          <div className="qb-mockup-card qb-mockup-card-ghost">
                            <div className="qb-mockup-ghost-line w70"></div>
                            <div className="qb-mockup-ghost-line w90"></div>
                            <div className="qb-mockup-ghost-line w60"></div>
                            <div className="qb-mockup-ghost-line w40 mt"></div>
                            <div className="qb-mockup-ghost-line w50"></div>
                          </div>
                        </div>
                      </div>
                    </main>
                  </div>
                </div>

                <div className="qb-cta-row">
                  <button
                    type="button"
                    className="qb-cta-primary"
                    onClick={() => {
                      // Sla brief op zodat ProjectCreatePage hem na register kan prefillen
                      try {
                        sessionStorage.setItem('pendingBrief', JSON.stringify({
                          description: briefDescription,
                          channels: briefChannels,
                          budget: parseFloat(briefBudget),
                          deadline: briefDeadline
                        }))
                      } catch (e) { /* sessionStorage niet beschikbaar */ }
                      navigate(isAuthenticated ? '/projects/create?fromQuickBrief=1' : '/register?role=bedrijf&intent=brief')
                    }}
                  >
                    {isAuthenticated ? 'Brief afmaken & plaatsen' : 'Account aanmaken & brief live zetten'}
                    <FontAwesomeIcon icon={faArrowRight} style={{ width: 14, height: 14, marginLeft: 8 }} />
                  </button>
                  <button
                    type="button"
                    className="qb-cta-secondary"
                    onClick={() => setShowBriefPreview(false)}
                  >
                    Aanpassen
                  </button>
                </div>
                <div className="qb-trust">
                  ✓ KvK-geverifieerd platform · ✓ Stripe escrow · ✓ 1000+ creators
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ==================== COVERFLOW PORTFOLIO SHOWCASE ==================== */}
      {portfolioCreators.length >= 3 && (
        <section className="cf-section">
          <div className="cf-head">
            <div className="sec-eyebrow purple" style={{ display: 'inline-flex' }}><span className="e-dot"></span> Portfolio's in actie</div>
            <h2>Echte content, <span className="prp">echte resultaten</span></h2>
            <p>Bekijk de portfolio's van onze top creators. Authentieke video's gemaakt voor merken zoals dat van jou.</p>
          </div>

          <div className="cf-stage" ref={cfStageRef}
            onTouchStart={onCfTouchStart}
            onTouchEnd={onCfTouchEnd}
            onTouchCancel={() => { cfTouchRef.current = null; cfRestartTimer() }}
            onMouseEnter={() => clearInterval(cfTimerRef.current)}
            onMouseLeave={() => {
              clearInterval(cfTimerRef.current)
              cfTimerRef.current = setInterval(() => {
                setCfCenter(c => (c + 1) % portfolioCreators.length)
              }, 4500)
            }}
          >
            <div className="cf-track">
              {portfolioCreators.map((c, i) => {
                const pos = cfPos(i)
                const video = c.portfolio.find(p => p?.videoUrl)
                const poster = video?.thumbnailUrl || c.profilePictureUrl
                return (
                  <div
                    style={{ cursor: pos === 0 ? 'default' : 'pointer' }}
                    aria-disabled="true"
                    key={c.id}
                    className={`cf-card ${pos === 0 ? 'playing' : ''}`}
                    data-pos={pos}
                    onClick={(e) => {
                      if (pos !== 0) {
                        e.preventDefault()
                        cfGoTo(i)
                      }
                    }}
                  >
                    <img className="cf-poster" src={poster} alt={firstName(c.name)} loading="lazy" />
                    <video
                      ref={el => cfVideoRefs.current[i] = el}
                      muted={i !== cfCenter || cfMuted}
                      loop
                      playsInline
                      preload="none"
                      poster={poster}
                    >
                      <source src={video.videoUrl} type="video/mp4" />
                    </video>
                    <div className="cf-top-grad"></div>
                    <div className="cf-overlay"></div>
                    <div className="cf-info">
                      <div className="cf-av" style={{ backgroundImage: `url('${c.profilePictureUrl}')` }}>
                        <div className={`cf-rank ${cfRank(c)}`}>★</div>
                      </div>
                      <div>
                        <div className="cf-name">{firstName(c.name)}{c.isAmbassador && <span style={{ fontSize: 11, marginLeft: 4 }}>👑</span>}</div>
                        <div className="cf-stars">★★★★★</div>
                      </div>
                    </div>
                    {isLive(c) && <div className="cf-live">Live</div>}
                    {pos === 0 && (
                      <button
                        type="button"
                        className="cf-mute"
                        aria-label={cfMuted ? 'Geluid aanzetten' : 'Geluid uitzetten'}
                        aria-pressed={!cfMuted}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setCfMuted(m => !m)
                        }}
                      >
                        {cfMuted ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
                        )}
                      </button>
                    )}
                    <div className="cf-bottom">
                      <div className="cf-niche">{nicheLabel(c)}</div>
                    </div>
                  </div>
                )
              })}
            </div>

            <button className="cf-nav cf-nav-prev" onClick={cfPrev} aria-label="Vorige creator">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button className="cf-nav cf-nav-next" onClick={cfNext} aria-label="Volgende creator">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>

          <div className="cf-meta">
            <div className="cf-now">
              <div className="cf-now-av" style={{ backgroundImage: `url('${portfolioCreators[cfCenter]?.profilePictureUrl}')` }}></div>
              <div className="cf-now-info">
                <div className="cf-now-name">{firstName(portfolioCreators[cfCenter]?.name)}</div>
                <div className="cf-now-niche">{nicheLabel(portfolioCreators[cfCenter])}{portfolioCreators[cfCenter]?.isAmbassador ? ' · Ambassador' : ''}</div>
              </div>
            </div>
            <div className="cf-dots">
              {portfolioCreators.map((_, i) => (
                <button
                  key={i}
                  className={`cf-dot ${i === cfCenter ? 'active' : ''}`}
                  aria-label={`Ga naar creator ${i + 1}`}
                  onClick={() => cfGoTo(i)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== FOUNDER'S PROMISE ==================== */}
      <section className="wrap">
        <div className="sec-head">
          <div className="sec-eyebrow"><span className="e-dot"></span> Net gestart, eerlijke missie</div>
          <h2>Wat we beloven aan <span className="grn">elk merk</span></h2>
        </div>
        <div className="founder-card">
          <span className="quote-mark">"</span>
          <p className="quote">
            Wij bouwen het eerlijkste UGC platform van Nederland. <b>85% gaat naar de creator</b>, transparant, zonder agency-fees of verborgen kosten. Een platform dat ik zelf zou willen gebruiken — voor merken én creators.
          </p>
          <div className="who">
            <div className="av-big">J</div>
            <div>
              <div className="name">Jayson</div>
              <div className="role">Founder · UGC4you</div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== STEPS ==================== */}
      <section className="wrap" id="hoe-werkt-het">
        <div className="sec-head">
          <div className="sec-eyebrow purple"><span className="e-dot"></span> Hoe werkt het</div>
          <h2>In 3 stappen <span className="prp">live</span></h2>
          <p>Geen ingewikkelde processen. Registreer, plaats of vind een opdracht, en ga aan de slag.</p>
        </div>
        <div className="steps-wrap">
          <div className="step-grid">
            <div className="step step-1">
              <div className="step-num"><span className="n">1</span> Stap 01</div>
              <div className="step-ico"><FontAwesomeIcon icon={faUserPlus} style={{ width: 22, height: 22 }} /></div>
              <h3>Maak je account aan</h3>
              <p>Gratis registreren als bedrijf of creator. <b>Binnen 60 seconden klaar.</b> Geen creditcard nodig.</p>
              <span className="tag"><FontAwesomeIcon icon={faBolt} style={{ width: 11, height: 11 }} /> 60 seconden</span>
            </div>
            <div className="step step-2">
              <div className="step-num"><span className="n">2</span> Stap 02</div>
              <div className="step-ico"><FontAwesomeIcon icon={faWandMagicSparkles} style={{ width: 22, height: 22 }} /></div>
              <h3>Plaats of vind een opdracht</h3>
              <p><b>Bedrijf:</b> Beschrijf wat je nodig hebt. <b>Creator:</b> Blader door opdrachten en reageer op wat past.</p>
              <span className="tag"><FontAwesomeIcon icon={faStarOfLife} style={{ width: 10, height: 10 }} /> Volledig vrij</span>
            </div>
            <div className="step step-3">
              <div className="step-num"><span className="n">3</span> Stap 03</div>
              <div className="step-ico"><FontAwesomeIcon icon={faVideo} style={{ width: 22, height: 22 }} /></div>
              <h3>Werk samen en lever op</h3>
              <p>Bespreek de briefing, maak de content en lever op. <b>Betaling veilig via het platform.</b></p>
              <span className="tag"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /> Veilig betalen</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WHY DIFFERENT ==================== */}
      <section className="wrap" id="eerlijk-model">
        <div className="sec-head">
          <div className="sec-eyebrow"><span className="e-dot"></span> Waarom anders</div>
          <h2>De meeste platformen nemen <span className="grn">30–50%</span>.<br />Wij draaien dat om.</h2>
        </div>

        <div className="why-grid">
          <div className="why-card w-hero">
            <div>
              <div className="badge"><span className="e-dot"></span> Voor de creator</div>
              <div className="pct">85%</div>
              <h3>Goed betaalde creators leveren betere content.</h3>
              <p>Wij rekenen slechts 15% platformkosten. Zo simpel is het. Geen agency-fees, geen verborgen kosten, geen minimumbedragen.</p>
            </div>
            <div className="receipt">
              <div className="rcp-row"><span>Opdracht</span><b>€150,00</b></div>
              <div className="rcp-row"><span>Platformkosten (15%)</span><b>−€22,50</b></div>
              <div className="rcp-row total"><span>Creator ontvangt</span><b>€127,50</b></div>
            </div>
          </div>

          <div className="why-card w-purple">
            <div className="w-ico"><FontAwesomeIcon icon={faBolt} style={{ width: 18, height: 18 }} /></div>
            <h4>Jij bepaalt het budget</h4>
            <p>Geen vaste pakketten of verborgen kosten. Stel je eigen prijs.</p>
            <ul>
              <li><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Geen minimumbedragen</li>
              <li><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Transparante prijzen</li>
              <li><span className="check"><FontAwesomeIcon icon={faCheck} style={{ width: 8, height: 8 }} /></span> Geen agency-fees</li>
            </ul>
          </div>

          <div className="why-card w-orange">
            <div className="w-ico"><FontAwesomeIcon icon={faStarOfLife} style={{ width: 16, height: 16 }} /></div>
            <h4>Kwaliteit door eerlijkheid</h4>
            <p>Geen race-to-the-bottom. Creators die zich gewaardeerd voelen leveren consistent betere content.</p>
          </div>

          <div className="why-card w-stat">
            <div className="w-ico"><FontAwesomeIcon icon={faClock} style={{ width: 18, height: 18 }} /></div>
            <div>
              <h4>Levertijd</h4>
              <div className="lbl">Snel, betrouwbaar, zonder gedoe.</div>
            </div>
            <div className="big">48<em>u</em></div>
          </div>

          <div className="why-card w-stat w-stat-2">
            <div className="w-ico" style={{ background: '#FEF3C7', color: '#B45309' }}><FontAwesomeIcon icon={faStar} style={{ width: 16, height: 16 }} /></div>
            <div>
              <h4>Klantscore</h4>
              <div className="w-stars">★★★★★</div>
              <div className="lbl">Vroege beoordelingen</div>
            </div>
            <div className="big">4.8<em>/5</em></div>
          </div>
        </div>
      </section>

      {/* ==================== TIKTOK VIDEO SHOWCASE ==================== */}
      <section className="wrap">
        <div className="sec-head">
          <div className="sec-eyebrow purple"><span className="e-dot"></span> UGC in actie</div>
          <h2>Zie de kracht van <span className="prp">UGC content</span></h2>
          <p>Authentieke video's van échte creators — gemaakt voor jouw merk, in jouw budget.</p>
        </div>

        <div className="video-showcase">
          <div className="video-phone-wrap">
            <div className="video-phone-glow"></div>
            <div className="video-phone">
              <div className="video-phone-inner">
                <div className="video-screen">
                  <div className="video-notch"><div className="nb"></div></div>
                  <div className="video-statusbar"><span>9:41</span><svg width="14" height="10" viewBox="0 0 24 24" fill="white"><path d="M1 7h6v10H1zm8 0h6v10H9zm8 0h6v10h-6z" /></svg></div>
                  <TikTokEmbed videoId="7267216887031860513" videoUrl="https://www.tiktok.com/@lisannekoning.ugc/video/7267216887031860513" />
                  <div className="video-home"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="video-content">
            <h3>Waarom UGC werkt:</h3>
            <p>Authentieke content scoort beter omdat kijkers het vertrouwen — geen geforceerde marketing, gewoon echte mensen.</p>
            <div className="video-reasons">
              <div className="video-reason">
                <div className="vri vri-1"><FontAwesomeIcon icon={faArrowTrendUp} style={{ width: 14, height: 14 }} /></div>
                <div>
                  <div className="vrt">Hogere conversie</div>
                  <div className="vrd">UGC voelt als een aanbeveling van een vriend, niet als een advertentie.</div>
                </div>
              </div>
              <div className="video-reason">
                <div className="vri vri-2"><FontAwesomeIcon icon={faHeart} style={{ width: 14, height: 14 }} /></div>
                <div>
                  <div className="vrt">Authentieke connectie</div>
                  <div className="vrd">Echte mensen, echte ervaringen — kijkers herkennen zichzelf in de content.</div>
                </div>
              </div>
              <div className="video-reason">
                <div className="vri vri-3"><FontAwesomeIcon icon={faSackDollar} style={{ width: 14, height: 14 }} /></div>
                <div>
                  <div className="vrt">Kosteneffectief</div>
                  <div className="vrd">Vanaf €75 per video. Geen studio, geen bureau, geen productiekosten.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PITCH — Voor bedrijven ==================== */}
      <section className="pitch" id="voor-wie">
        <div className="pitch-card">
          <div className="pitch-img">
            <img src="/ugc-hero-2.webp" alt="Bedrijfseigenaar" />
            <div className="quote-bubble">
              <b>Esra · creator</b>
              Klinkt goed! Ik kan dat maken. Zal ik een voorstel sturen?
            </div>
            <div className="pitch-bottom">
              <div>
                <div className="name">Esra & jouw merk</div>
                <div className="role">Briefing geaccepteerd · 14:02</div>
              </div>
              <div className="safety">Veilige betaling</div>
            </div>
          </div>
          <div className="pitch-content">
            <div className="sec-eyebrow"><span className="e-dot"></span> Voor bedrijven</div>
            <h2>Stop met betalen voor content die <span className="grn">niemand bekijkt.</span></h2>
            <p>Stel je eigen budget in, ontvang binnen 24 uur voorstellen van creators, en krijg authentieke video's. Betaal pas na goedkeuring.</p>
            <ul className="pitch-list">
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Jij stelt het budget in, geen vaste pakketten</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> 1000+ creators uit elke niche</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Betaling pas na goedkeuring</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Geen agency-kosten of verborgen fees</li>
            </ul>
            <Link to="/register?role=business" className="btn btn-green btn-lg">
              Start gratis — eerste opdracht in 5 min <FontAwesomeIcon icon={faArrowRight} style={{ width: 13, height: 13 }} />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== CREATORS — Voor creators ==================== */}
      <section className="creators-section">
        <div className="cr-grid">
          <div className="cr-visual">
            <img src="/ugc-hero-4.webp" alt="UGC creator filmt content" />
            <div className="commission-badge"><small>Jouw commissie</small><b>85%</b></div>
            <div className="week-badge"><small>Deze week</small><b>€425,00</b></div>
            <div className="cr-tag">Voor creators</div>
          </div>
          <div className="cr-content">
            <div className="sec-eyebrow purple"><span className="e-dot"></span> Voor creators</div>
            <h2>Verdien wat je <span className="prp">waard bent</span></h2>
            <p>Op andere platformen verlies je 30–50% aan commissie. Bij ons houd je <b>85%</b>. Bepaal je eigen tarieven en krijg wekelijks uitbetaald.</p>
            <ul className="cr-list">
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> 85% van elke opdracht</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Bepaal eigen tarieven</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Wekelijkse uitbetaling</li>
              <li><span className="ck"><FontAwesomeIcon icon={faCheck} style={{ width: 10, height: 10 }} /></span> Alles op 1 plek</li>
            </ul>
            <Link to="/register?role=creator" className="btn btn-primary btn-lg">
              Start als creator <FontAwesomeIcon icon={faArrowRight} style={{ width: 13, height: 13 }} />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== CATEGORIES — Producten/Diensten/Locaties ==================== */}
      <section className="wrap">
        <div className="sec-head">
          <div className="sec-eyebrow orange"><span className="e-dot"></span> UGC voor elk merk</div>
          <h2>Niet alleen voor <span className="org">producten</span></h2>
          <p>Verkoop je geen fysiek product? Onze creators lichten ook diensten en locaties uit met authentieke video's die jouw merk laten leven.</p>
        </div>
        <div className="cats-grid">
          <div className="cat-card cat-prod">
            <div className="cat-head">
              <div className="cat-ico ic-prod"><FontAwesomeIcon icon={faBox} style={{ width: 20, height: 20 }} /></div>
              <h3>Producten</h3>
            </div>
            <p>Toon je product in actie met authentieke video's die converteren.</p>
            <div className="cat-tags">
              <span className="cat-tag">Beauty</span>
              <span className="cat-tag">Mode</span>
              <span className="cat-tag">Food</span>
              <span className="cat-tag">Tech</span>
            </div>
          </div>
          <div className="cat-card cat-serv">
            <div className="cat-head">
              <div className="cat-ico ic-serv"><FontAwesomeIcon icon={faWandMagicSparkles} style={{ width: 20, height: 20 }} /></div>
              <h3>Diensten</h3>
            </div>
            <p>Maak je dienst tastbaar met testimonials en explainer-video's.</p>
            <div className="cat-tags">
              <span className="cat-tag">Coaching</span>
              <span className="cat-tag">Wellness</span>
              <span className="cat-tag">Salon</span>
              <span className="cat-tag">B2B-services</span>
            </div>
          </div>
          <div className="cat-card cat-loc">
            <div className="cat-head">
              <div className="cat-ico ic-loc"><FontAwesomeIcon icon={faLocationDot} style={{ width: 20, height: 20 }} /></div>
              <h3>Locaties</h3>
            </div>
            <p>Laat zien hoe jouw plek voelt — perfect voor lokale marketing.</p>
            <div className="cat-tags">
              <span className="cat-tag">Horeca</span>
              <span className="cat-tag">Hotels</span>
              <span className="cat-tag">Retail</span>
              <span className="cat-tag">Gyms</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="final">
        <div className="final-card">
          <div className="sec-eyebrow"><span className="e-dot"></span> Klaar om te starten?</div>
          <h2>Plaats je eerste opdracht<br /><span className="grn">vandaag nog.</span></h2>
          <p>50+ creators staan klaar. Geen verplichtingen. Betaal alleen wat je goedkeurt.</p>
          <div className="cta-row">
            <Link to="/register?role=business" className="btn btn-green btn-lg">
              Plaats je eerste opdracht <FontAwesomeIcon icon={faArrowRight} style={{ width: 13, height: 13 }} />
            </Link>
            <Link to="/register?role=creator" className="btn btn-glass btn-lg">Of meld je aan als creator</Link>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <Logo />
            <p>Het eerlijkste UGC platform van Nederland. 85% voor creators, transparante prijzen voor bedrijven.</p>
          </div>
          <div className="footer-col">
            <h5>Voor bedrijven</h5>
            <a href="/register?role=business">Opdrachten plaatsen</a>
            <a href="/creators">Zoek creators</a>
            <a href="/ugc-prijzen">Prijzen</a>
            <a href="/ugc-voorbeelden">UGC Voorbeelden</a>
          </div>
          <div className="footer-col">
            <h5>Voor creators</h5>
            <a href="/register?role=creator">Word creator</a>
            <a href="/ugc-tips-creators">Tips voor creators</a>
            <a href="/ugc-content-creators">Creator hub</a>
          </div>
          <div className="footer-col">
            <h5>Support &amp; info</h5>
            <a href="/wat-is-ugc">Wat is UGC?</a>
            <a href="/contact">Contact</a>
            <a href="/privacy-policy">Privacy</a>
            <a href="/algemene-voorwaarden">Voorwaarden</a>
          </div>
        </div>

        {/* SEO link-hub: linkt belangrijke landingspagina's vanaf de homepage (hoogste autoriteit) */}
        <div className="footer-hub">
          <div>
            <h5>UGC per branche</h5>
            <a href="/ugc-voor-webshops">Webshops</a>
            <a href="/ugc-voor-restaurants">Restaurants</a>
            <a href="/ugc-voor-beauty-merken">Beauty merken</a>
            <a href="/ugc-voor-fitness">Fitness</a>
            <a href="/ugc-voor-mode">Mode</a>
            <a href="/ugc-voor-tech">Tech &amp; SaaS</a>
            <a href="/ugc-voor-horeca">Horeca</a>
            <a href="/ugc-voor-reisbranche">Reisbranche</a>
            <a href="/ugc-voor-vastgoed">Vastgoed</a>
            <a href="/ugc-voor-huisdieren">Huisdieren</a>
            <a href="/ugc-voor-coaches-dienstverleners">Coaches</a>
            <a href="/ugc-voor-salons-wellness">Salons &amp; Wellness</a>
            <a href="/ugc-b2b">B2B</a>
          </div>
          <div>
            <h5>Video laten maken</h5>
            <a href="/product-video-laten-maken">Productvideo</a>
            <a href="/advertentie-video-laten-maken">Advertentievideo</a>
            <a href="/testimonial-video-laten-maken">Testimonialvideo</a>
            <a href="/explainer-video-laten-maken">Explainervideo</a>
            <a href="/professionele-video-laten-maken">Professionele video</a>
            <a href="/social-media-content-laten-maken">Social media content</a>
            <a href="/voor-bedrijven">UGC laten maken</a>
            <a href="/video-ugc">Video UGC</a>
          </div>
          <div>
            <h5>Social media uitbesteden</h5>
            <a href="/social-media-uitbesteden">Social media uitbesteden</a>
            <a href="/social-media-beheer-uitbesteden">Social media beheer</a>
            <a href="/social-media-marketing-uitbesteden">Social media marketing</a>
            <a href="/social-media-adverteren-uitbesteden">Adverteren uitbesteden</a>
            <a href="/ugc-campagne-opzetten">Campagne opzetten</a>
            <a href="/ugc-marketing-strategie">Marketingstrategie</a>
          </div>
          <div>
            <h5>Voor creators</h5>
            <a href="/ugc-creator-worden">Creator worden</a>
            <a href="/ugc-als-bijbaan">UGC als bijbaan</a>
            <a href="/ugc-briefing-schrijven">Briefing schrijven</a>
            <a href="/ugc-script-schrijven">Script schrijven</a>
            <a href="/ugc-portfolio-opbouwen">Portfolio opbouwen</a>
            <a href="/ugc-video-maken-smartphone">Filmen met smartphone</a>
            <a href="/ugc-creator-vacature">Vacatures</a>
            <a href="/ugc-rechten-licenties">Rechten &amp; licenties</a>
          </div>
          <div>
            <h5>UGC per stad</h5>
            <a href="/ugc-creator-amsterdam">Amsterdam</a>
            <a href="/ugc-creator-rotterdam">Rotterdam</a>
            <a href="/ugc-creator-den-haag">Den Haag</a>
            <a href="/ugc-creator-utrecht">Utrecht</a>
            <a href="/ugc-creator-eindhoven">Eindhoven</a>
            <a href="/ugc-creator-nederland">Heel Nederland</a>
          </div>
          <div>
            <h5>Vergelijken &amp; meer</h5>
            <a href="/ugc4you-vs-influee">UGC4you vs Influee</a>
            <a href="/ugc4you-vs-speekly">UGC4you vs Speekly</a>
            <a href="/ugc4you-vs-hyred">UGC4you vs Hyred</a>
            <a href="/ugc-platform-vergelijken">Platforms vergelijken</a>
            <a href="/wat-kost-ugc-creator-nederland">Wat kost een creator?</a>
            <a href="/ai-ugc-video-tools">AI UGC tools</a>
            <a href="/over-ons">Over ons</a>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© 2026 UGC4you · Nederlands platform</span>
          <div className="right">
            <a href="/privacy-policy">Privacy</a>
            <a href="/algemene-voorwaarden">Voorwaarden</a>
            <a href="/contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default NewHomePage
